import { SelectOption } from "@via/utils/interfaces";
import {
  CreateVmetaConfigApiRequestBody,
  UpdateVmetaConfigApiRequestBody,
  VmetaConfigApi,
} from "common";

// ── Types ────────────────────────────────────────────────────────────────────

export type MappingField = {
  select: string;
  rename: string;
};

export type JoinForm = {
  source_collection: string;
  source_field: string;
  foreign_field: string;
};

export type ExtraGroupForm = {
  collectionID: string;
  enableJoin: boolean;
  join: JoinForm;
  fields: MappingField[];
};

export type ApiConfigFormValues = {
  route: string;
  collectionID: string;
  targetFields: MappingField[];
  groups: ExtraGroupForm[];
};

// ── Defaults ─────────────────────────────────────────────────────────────────

export const defaultFieldMapping = (): MappingField => ({
  select: "",
  rename: "",
});

export const defaultJoin = (): JoinForm => ({
  source_collection: "",
  source_field: "",
  foreign_field: "",
});

export const SYSTEM_FIELD_OPTIONS: SelectOption[] = [
  {
    label: "Document ID (_id)",
    value: "_id",
  },
];

export const defaultGroup = (): ExtraGroupForm => ({
  collectionID: "",
  enableJoin: false,
  join: defaultJoin(),
  fields: [defaultFieldMapping()],
});

// ── Converters ───────────────────────────────────────────────────────────────

export const mapPairsToFields = (
  select: string[] = [],
  rename: string[] = []
): MappingField[] => {
  if (select.length === 0 && rename.length === 0) {
    return [defaultFieldMapping()];
  }

  const len = Math.max(select.length, rename.length);
  return Array.from({ length: len }, (_, index) => ({
    select: select[index] ?? "",
    rename: rename[index] ?? "",
  }));
};

export const toAutoPopulatedFieldMappings = (
  options: SelectOption[] = []
): MappingField[] =>
  options.map(option => ({
    select: String(option.value),
    rename: String(option.value),
  }));

export const toFormValues = (
  config?: VmetaConfigApi | null
): ApiConfigFormValues => {
  if (!config) {
    return {
      route: "",
      collectionID: "",
      targetFields: [defaultFieldMapping()],
      groups: [],
    };
  }

  const rawConfig = Array.isArray(config.raw_config) ? config.raw_config : [];

  const targetGroup = rawConfig.find(
    row => row.collectionID === config.collectionID
  );
  const extraGroups = rawConfig.filter(
    row => row.collectionID !== config.collectionID
  );

  return {
    route: config.route ?? "",
    collectionID: config.collectionID ?? "",
    targetFields: mapPairsToFields(targetGroup?.select, targetGroup?.rename),
    groups: extraGroups.map(group => ({
      collectionID: group.collectionID,
      enableJoin: !!group.join?.[0],
      join: group.join?.[0]
        ? {
            source_collection: group.join[0].source_collection,
            source_field: group.join[0].source_field,
            foreign_field: group.join[0].foreign_field,
          }
        : defaultJoin(),
      fields: mapPairsToFields(group.select, group.rename),
    })),
  };
};

export const sanitizeFields = (fields: MappingField[]) =>
  fields.filter(field => field.select && field.rename);

export const toPayload = (
  values: ApiConfigFormValues
): CreateVmetaConfigApiRequestBody | UpdateVmetaConfigApiRequestBody => {
  const targetFields = sanitizeFields(values.targetFields);
  const groups = values.groups
    .filter(group => group.collectionID)
    .map(group => {
      const row = {
        collectionID: group.collectionID,
        select: sanitizeFields(group.fields).map(field => field.select),
        rename: sanitizeFields(group.fields).map(field => field.rename),
      } as CreateVmetaConfigApiRequestBody["raw_config"][number];

      if (
        group.enableJoin &&
        group.join.source_collection &&
        group.join.source_field &&
        group.join.foreign_field
      ) {
        row.join = [
          {
            source_collection: group.join.source_collection,
            source_field: group.join.source_field,
            foreign_field: group.join.foreign_field,
          },
        ];
      }

      return row;
    });

  return {
    collectionID: values.collectionID,
    route: values.route,
    raw_config: [
      {
        collectionID: values.collectionID,
        select: targetFields.map(field => field.select),
        rename: targetFields.map(field => field.rename),
      },
      ...groups,
    ],
  };
};
